import React from 'react';
import {
    Box,
    Chip,
    IconButton,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import dayjs from 'dayjs';
import {
    BUSINESS_TZ,
    DATE_FORMAT,
    formatDate,
    formatFloat,
    getDateFromString,
    useTouchDoubleActivation,
} from '../../../../../helpers';
import {
    PlanningCoverage,
    PlanningItem,
    PlanningOrder,
} from '../orders-to-fulfill';

export type PlanningMove = 'up' | 'down' | 'top';

// Spells out how "Inv" got to its number. The figure on the card is what is
// left for THIS pedido, which is normally lower than the warehouse count — and
// without the breakdown that difference just looks like a wrong number.
function inventoryTooltip(item: PlanningItem): string {
    const unit = item.unit;
    const consumed = item.inventory - item.committed - item.available;
    return [
        `Inventario en almacén: ${formatFloat(item.inventory, 0)} ${unit}`,
        item.committed > 0
            ? `Comprometido en ventas por entregar: ${formatFloat(
                  item.committed,
                  0,
              )} ${unit}`
            : null,
        consumed > 0
            ? `Tomado por pedidos con mayor prioridad: ${formatFloat(
                  consumed,
                  0,
              )} ${unit}`
            : null,
        `Disponible para este pedido: ${formatFloat(item.available, 0)} ${unit}`,
    ]
        .filter((part): part is string => part !== null)
        .join(' · ');
}

// `partial` is a golden yellow rather than MUI's orange `warning.main`: as a
// small dot the orange sat too close to `error.main`'s red to tell apart. Keep
// in sync with the planeación drawer's partial chip (pedidos-drawer.tsx).
export const COVERAGE_PARTIAL_COLOR = '#eab308';

export const coverageColor: Record<PlanningCoverage, string> = {
    full: 'success.main',
    partial: COVERAGE_PARTIAL_COLOR,
    none: 'error.main',
};

export function CoverageDot({ coverage }: { coverage: PlanningCoverage }) {
    return (
        <Box
            component={'span'}
            sx={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: coverageColor[coverage],
                ml: 1,
                flexShrink: 0,
            }}
        />
    );
}

// estimated_delivery_date is a CALENDAR DATE (floating, midnight UTC), so read
// the day back with getDateFromString and compare with Mexico's business day.
function getDeliveryInfo(date: string | null): { label: string; color: string } {
    if (!date) {
        return { label: 'Sin fecha estimada', color: 'text.disabled' };
    }
    const deliveryDay = dayjs.utc(getDateFromString(date));
    const today = dayjs.utc(dayjs().tz(BUSINESS_TZ).format(DATE_FORMAT));
    const days = deliveryDay.diff(today, 'day');
    const formatted = formatDate(date);
    if (days < 0) {
        return { label: `Entrega ${formatted} (atrasada)`, color: 'error.main' };
    }
    if (days === 0) {
        return { label: 'Entrega hoy', color: 'error.main' };
    }
    if (days <= 3) {
        return {
            label: `Entrega ${formatted} (en ${days} día${days === 1 ? '' : 's'})`,
            color: 'warning.main',
        };
    }
    return { label: `Entrega ${formatted}`, color: 'text.secondary' };
}

export function PlanningOrderCard({
    order,
    canReorder = false,
    isSaving = false,
    isFirstInBucket = true,
    isLastInBucket = true,
    onMove,
    canChangeStatus = false,
    onChangeStatus,
}: {
    order: PlanningOrder;
    canReorder?: boolean;
    isSaving?: boolean;
    isFirstInBucket?: boolean;
    isLastInBucket?: boolean;
    onMove?: (order: PlanningOrder, move: PlanningMove) => void;
    canChangeStatus?: boolean;
    onChangeStatus?: (order: PlanningOrder) => void;
}) {
    const isInProduction = order.order_request_status_id === 2;
    const delivery = getDeliveryInfo(order.order_request_estimated_delivery_date);
    const handleStatusTouchEnd = useTouchDoubleActivation(
        canChangeStatus && onChangeStatus
            ? () => onChangeStatus(order)
            : undefined,
    );

    return (
        <Paper
            variant={'outlined'}
            sx={{ p: 2, display: 'flex', flexDirection: 'column' }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 500 }}>
                        {order.order_code !== null ? order.order_code : '—'}
                    </Typography>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {order.account_name}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        flexShrink: 0,
                    }}
                >
                    {canReorder && onMove && (
                        <Box sx={{ display: 'flex' }}>
                            <Tooltip title={'Subir al inicio'}>
                                <span>
                                    <IconButton
                                        size={'small'}
                                        disabled={isSaving || isFirstInBucket}
                                        onClick={() => onMove(order, 'top')}
                                    >
                                        <VerticalAlignTopIcon fontSize={'inherit'} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={'Subir'}>
                                <span>
                                    <IconButton
                                        size={'small'}
                                        disabled={isSaving || isFirstInBucket}
                                        onClick={() => onMove(order, 'up')}
                                    >
                                        <KeyboardArrowUpIcon fontSize={'inherit'} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={'Bajar'}>
                                <span>
                                    <IconButton
                                        size={'small'}
                                        disabled={isSaving || isLastInBucket}
                                        onClick={() => onMove(order, 'down')}
                                    >
                                        <KeyboardArrowDownIcon fontSize={'inherit'} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                    <Tooltip
                        title={
                            canChangeStatus
                                ? 'Doble clic o doble toque para cambiar status'
                                : ''
                        }
                    >
                        <Chip
                            size={'small'}
                            variant={'outlined'}
                            color={isInProduction ? 'info' : 'warning'}
                            label={order.order_request_status_name}
                            onDoubleClick={
                                canChangeStatus && onChangeStatus
                                    ? () => onChangeStatus(order)
                                    : undefined
                            }
                            onTouchEnd={
                                canChangeStatus && onChangeStatus
                                    ? handleStatusTouchEnd
                                    : undefined
                            }
                            sx={{
                                cursor: canChangeStatus ? 'pointer' : 'default',
                                touchAction: canChangeStatus
                                    ? 'manipulation'
                                    : undefined,
                            }}
                        />
                    </Tooltip>
                </Box>
            </Box>
            <Typography
                variant={'caption'}
                color={'text.secondary'}
                sx={{ mt: 0.5 }}
            >
                Solicitado: {formatDate(order.order_request_date)}
            </Typography>
            <Typography variant={'caption'} sx={{ color: delivery.color }}>
                {delivery.label}
            </Typography>
            <Box sx={{ mt: 1 }}>
                {order.items.map((item, index) => (
                    <Box
                        key={`${item.product_id}-${index}`}
                        sx={{
                            py: 0.5,
                            ...(index > 0 && {
                                borderTop: '1px solid',
                                borderColor: 'divider',
                            }),
                            // delivered lines stay visible (restante 0) but fade
                            // back so open lines carry the visual weight
                            opacity: item.isComplete ? 0.55 : 1,
                        }}
                    >
                        {/* Name takes the full card width and clamps to 2 lines;
                            the full description is available on hover. */}
                        <Tooltip title={item.product_description}>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    lineHeight: 1.2,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}
                            >
                                {item.product_description}
                            </Typography>
                        </Tooltip>
                        {/* Compact numbers row beneath the name */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 0.25,
                            }}
                        >
                            {/* Solicitado is what the pedido asked for;
                                Restante is what is still uncovered after the
                                delivered sales AND this line's inventory
                                allocation (requested − sold − inventario). */}
                            <Box>
                                <Typography
                                    variant={'caption'}
                                    color={'text.secondary'}
                                    sx={{ display: 'block' }}
                                >
                                    Solicitado:{' '}
                                    {formatFloat(item.requested, 0)} {item.unit}
                                </Typography>
                                <Typography
                                    variant={'caption'}
                                    color={'text.secondary'}
                                    sx={{ display: 'block' }}
                                >
                                    Restante:{' '}
                                    <Box
                                        component={'span'}
                                        sx={{
                                            color: 'text.primary',
                                            fontWeight: 500,
                                        }}
                                    >
                                        {formatFloat(
                                            Math.max(
                                                0,
                                                item.remaining - item.covered,
                                            ),
                                            0,
                                        )}{' '}
                                        {item.unit}
                                    </Box>
                                </Typography>
                            </Box>
                            {item.isComplete ? (
                                <Tooltip title={'Entregado'}>
                                    <CheckCircleOutlineIcon
                                        fontSize={'small'}
                                        color={'success'}
                                        sx={{ verticalAlign: 'middle' }}
                                    />
                                </Tooltip>
                            ) : !item.inventoryTracked ? (
                                // Pellet/lavado: no consumption is captured, so
                                // there is no inventory row and "Inv: 0" with a
                                // red dot would assert a shortfall nobody can
                                // confirm. Show a neutral chip instead — the same
                                // wording the planeación drawer uses.
                                <Tooltip
                                    title={
                                        'Este tipo de producción no registra consumo, así que no hay dato de inventario (pellet y compactado)'
                                    }
                                >
                                    <Chip
                                        size={'small'}
                                        variant={'outlined'}
                                        label={'sin dato de inventario'}
                                    />
                                </Tooltip>
                            ) : (
                                // `available`, NOT the raw inventory: the pool
                                // is consumed in queue order, so this is what
                                // earlier pedidos actually left for this one.
                                // Showing the raw figure printed the same number
                                // on every card and hid the cascade entirely.
                                <Tooltip title={inventoryTooltip(item)}>
                                    <Typography
                                        variant={'caption'}
                                        color={'text.secondary'}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        Inv: {formatFloat(item.available, 0)}{' '}
                                        {item.unit}
                                        <CoverageDot coverage={item.coverage} />
                                    </Typography>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
}
